import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Modal } from 'flowbite';
import { IBase, IClient, IGtfsImportWarning, ITown } from 'src/app/models';
import { AppsService, LiteralService, UsersService, UtilsService } from 'src/app/services';
import { images } from 'src/images';
import { TooltipPosition } from '../../tooltip/tooltip-position';
import { AnimationOptions } from 'ngx-lottie';

@Component({
  selector: 'app-modal-gtfs',
  templateUrl: './modal-gtfs.component.html',
  styleUrl: './modal-gtfs.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ModalGtfsComponent implements OnInit {
  @Input() id: string;
  @Input() modal: Modal;
  @Input() edit: boolean = false;
  @Input() loading: boolean = false;
  @Input() loadingPercentage: number = 0;
  @Input() loadingDetail: string = '';
  @Input() warnings: IGtfsImportWarning[] = [];
  @Output() action = new EventEmitter<any>();
  @Output() closeSummary = new EventEmitter<void>();

  @ViewChild('form') form: ElementRef;
  public formGroup: FormGroup = new FormGroup({});

  public agency = new FormControl('');
  public routes = new FormControl('');
  public stops = new FormControl('');
  public stopsTimes = new FormControl('');
  public trips = new FormControl('');
  public calendar = new FormControl('');
  public calendarDates = new FormControl('');
  public shapes = new FormControl('');

  public towns: any[] = [];
  public clients: any[] = [];
  public apps: any[] = [];
  public selectedAppIds: number[] = [];

  public TooltipPosition = TooltipPosition;
  public document = document;
  public images = images;
  // Mandatory: routes, trips, stops, stopTimes, calendar. Optional: calendarDates, agency, shapes.
  // 8 items render as a 4-rows x 2-cols grid (first 4 -> left column, last 4 -> right column).
  public uploaded = [
    { type: 'routes', value: false, file: { fileName: '', value: {} as File}, formControl: this.routes, required: true },
    { type: 'trips', value: false, file: { fileName: '', value: {} as File}, formControl: this.trips, required: true },
    { type: 'stops', value: false, file: { fileName: '', value: {} as File}, formControl: this.stops, required: true },
    { type: 'stopTimes', value: false, file: { fileName: '', value: {} as File}, formControl: this.stopsTimes, required: true },
    { type: 'calendar', value: false, file: { fileName: '', value: {} as File}, formControl: this.calendar, required: true },
    { type: 'calendarDates', value: false, file: { fileName: '', value: {} as File}, formControl: this.calendarDates, required: false },
    { type: 'agency', value: false, file: { fileName: '', value: {} as File}, formControl: this.agency, required: false },
    { type: 'shapes', value: false, file: { fileName: '', value: {} as File}, formControl: this.shapes, required: false },
  ];

  public selectTown = true;

  public snapToRoad = false;
  public shapesLoaded = false;
  public agencyLoaded = false;

  public importing: AnimationOptions = {
    path: '/assets/animations/importing.json',
  };

  constructor(
    public literalService: LiteralService,
    private usersService: UsersService,
    public utilsService: UtilsService,
    private appsService: AppsService,
  ) {}

  async ngOnInit() {
    await this.usersService.getPossibleClientTowns().then((res) => {
      res.towns.sort(
        (firstItem: IBase, secondItem: IBase) => firstItem.id! - secondItem.id!,
      );
      res.clients.forEach((client: IClient) => {
        this.clients.push({
          id: client.id,
          value: client.id,
          label: client.name,
        });
      });
    });
    this.formGroup = new FormGroup({});
    this.setControls();
  }

  async setControls() {
    this.uploaded.forEach((u: any) => {
      const validators = u.required ? [Validators.required] : [];
      const control = new FormControl('', validators);
      u.formControl = control;
      this.formGroup.addControl(u.type, control);
    });
    if (this.selectTown) {
      this.formGroup.removeControl('townName');
      this.formGroup.addControl(
        'townId',
        new FormControl('', [Validators.required]),
      );
    } else {
      this.formGroup.removeControl('townId');
      this.formGroup.addControl(
        'townName',
        new FormControl('', [Validators.required]),
      );
    }
    this.formGroup.addControl(
      'clientId',
      new FormControl('', [Validators.required]),
    );
    if (this.clients.length === 1) {
      this.formGroup.get('clientId')?.setValue(this.clients[0].id);
      await this.loadTowns(this.clients[0].id!);
      await this.loadApps(this.clients[0].id!);
    }
  }

  onFileSelected(event: any, type: string) {
    const file: File = event.target.files[0];
    if (!file) return;

    const fileName = file.name;
    const uploadedItem = this.uploaded.find((u: any) => u.type === type);

    if (!uploadedItem) return;

    uploadedItem.file = { fileName, value: file };
    uploadedItem.value = true;

    const control = this.formGroup.get(type);
    control?.setValue(fileName);
    control?.markAsTouched();
    control?.updateValueAndValidity();

    // A shapes file defines the route geometry on the backend regardless of the snap-to-roads
    // option, so lock the checkbox off once shapes are provided.
    if (type === 'shapes') {
      this.shapesLoaded = true;
      this.snapToRoad = false;
    }
    if (type === 'agency') this.enterAgencyMode();
  }

  get clientSelected(): boolean {
    return !!this.formGroup.get('clientId')?.value;
  }

  // agency.txt only serves to create the client, so it is disabled once one is selected
  isFileDisabled(item: any): boolean {
    return item.type === 'agency' && this.clientSelected;
  }

  fileTooltip(item: any): string {
    if (this.isFileDisabled(item)) return this.literalService.get('services.modal.agencyNotNeeded', true);
    return item.file.fileName;
  }

  get warningsByRoute(): { routeName: string; warnings: IGtfsImportWarning[] }[] {
    const groups = new Map<string, IGtfsImportWarning[]>();
    this.warnings.forEach((warning: IGtfsImportWarning) => {
      const routeWarnings = groups.get(warning.routeName) ?? [];
      routeWarnings.push(warning);
      groups.set(warning.routeName, routeWarnings);
    });
    return Array.from(groups.entries()).map(([routeName, warnings]) => ({ routeName, warnings }));
  }

  warningText(warning: IGtfsImportWarning): string {
    return this.literalService.get(`services.modal.importGTFS.summary.types.${warning.type}`, true, {
      trip: warning.tripId,
      stops: (warning.stopNames ?? []).join(', ')
    });
  }

  // Uploading agency.txt before choosing a client means the backend creates the client from the file:
  // the client selector is locked and the town can only be a new one
  private enterAgencyMode() {
    this.agencyLoaded = true;
    const clientControl = this.formGroup.get('clientId');
    clientControl?.clearValidators();
    clientControl?.setValue('');
    clientControl?.updateValueAndValidity();
    this.towns = [];
    this.apps = [];
    this.selectedAppIds = [];
    if (this.selectTown) this.changeCreationType(true);
  }

  async setClientId(event: any) {
    this.formGroup.get('clientId')?.setValue(event.value);
    await this.loadTowns(event.value);
    await this.loadApps(event.value);
  }

  private async loadTowns(clientId: number) {
    this.towns = [];
    // Towns without routes yet are valid import targets
    await this.usersService.getTownsByClient([clientId], false).then((res) => {
      const towns = res as ITown[];
      towns.forEach((town: ITown) => {
        this.towns.push({ id: town.id, value: town.id, label: town.name });
      });
    });
  }

  private async loadApps(clientId: number) {
    this.apps = [];
    this.selectedAppIds = [];
    await this.appsService.getAppsByClient(clientId).then((res) => {
      (res as any[]).forEach((app: any) => {
        this.apps.push({ id: app.id, value: app.id, label: app.name });
      });
    });
  }

  setAppIds(event: any) {
    this.selectedAppIds = event.options
      ? event.options.map((o: any) => o.id)
      : Array.isArray(event.value)
        ? event.value
        : [];
  }

  // Render the selected apps as a single ellipsised line ("App A; App B; …")
  // instead of the default stacked chips.
  appsSelectionLabel(): string {
    return this.selectedAppIds
      .map((id: number) => this.apps.find((a: any) => a.id === id)?.label)
      .filter(Boolean)
      .join('; ');
  }

  setTownId(event: any) {
    this.formGroup.get('townId')?.setValue(event.value);
  }

  setTownName(event: any) {
    this.formGroup.get('townName')?.setValue(event.target.value);
  }

  changeCreationType(create?: boolean) {
    this.selectTown = !this.selectTown;
    if (create) {
      this.formGroup.removeControl('townId');
      this.formGroup.addControl(
        'townName',
        new FormControl('', [Validators.required]),
      );
    } else {
      this.formGroup.removeControl('townName');
      this.formGroup.addControl(
        'townId',
        new FormControl('', [Validators.required]),
      );
    }
  }

  changeSnapToRoad(event: any) {
    this.snapToRoad = event.target.checked;
  }

  onSubmit() {
    const files = this.uploaded.filter((u: any) => u.value);
    let data = {files: files, clientId: this.formGroup.get('clientId')?.value || undefined, townId: this.formGroup.get('townId')?.value, snapToRoad: this.snapToRoad, appIds: this.selectedAppIds};
    Object.assign(data, this.selectTown ? {townId: this.formGroup.get('townId')?.value} : {townName: this.formGroup.get('townName')?.value})
    this.action.emit(data);
  }
}
